<template>
  <div>
    <p v-if="!hidePromotionTypeHeader" :class="$style.promotionDescription">
      {{ t('toplisting.modal.promotionType.line1') }}
    </p>
    <slot name="afterPromotionDescription" />
    <div class="row" :class="$style.promotionRow">
      <div v-for="(item, index) in cards" :key="item.value" class="col-sm" :class="$style.colCell">
        <div :class="$style.cardColumn">
          <div :class="$style.badgeRail">
            <HgBadge
              v-if="item.value === ToplistingVariant.Top && !hideRecommendedBadge"
              :text="t('toplisting.modal.promotionType.badge')"
              theme="working"
            />
          </div>
          <div :class="$style.cardGrow">
            <PromotionTypeCard
              fillColumnHeight
              :name="radioName"
              :inputValue="item.value"
              :modelValue="modelValue"
              :header="t(`toplisting.modal.options.${item.i18nKey}.header`)"
              :intro="introFor(item)"
              :benefitLines="benefitLinesFor(item)"
              :disabled="item.disabled"
              :displayPricingSlot="!!item.showPricingSlot"
              :accentBorderColor="item.accentColor"
              :selectedBackgroundColor="selectedBackgroundForIndex(index)"
              :improvedBenefits="useImprovedBenefitLines"
              :showBenefitLine2Footnote="showBenefitLine2FootnoteFor(item)"
              :benefitLine2Footnote="t('toplisting.modal.promotionType.line2ImprovedFootnote')"
              @update:modelValue="onSelect($event)"
            >
              <slot v-if="item.showPricingSlot" :name="pricingSlotName(item.i18nKey)"></slot>
            </PromotionTypeCard>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import PromotionTypeCard from '@/components/PromotionTypeCard/PromotionTypeCard.vue';
import type { ToplistingPromotionCardConfig } from '@/components/ToplistingPromotionCards/types';
import { TOPLISTING_PROMOTION_CARD_SELECTED_BACKGROUNDS } from '@/constants/toplistingPromotion';
import { ToplistingVariant } from '@/types';
import { HgBadge } from '@smg-real-estate/domus';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    cards: ToplistingPromotionCardConfig[];
    radioName?: string;
    hidePromotionTypeHeader?: boolean;
    useImprovedBenefitLines?: boolean;
    /** Hides the "recommended" badge on the Top Listing card (e.g. modal edit of an existing booking). */
    hideRecommendedBadge?: boolean;
  }>(),
  {
    radioName: 'toplistingPromotionCards',
    hidePromotionTypeHeader: false,
    useImprovedBenefitLines: false,
    hideRecommendedBadge: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const { t, te } = useI18n();

function pricingSlotName(i18nKey: string): string {
  return `pricing-${i18nKey}`;
}

function benefitLineKey(prefix: string, lineNum: 1 | 2 | 3): string {
  if (props.useImprovedBenefitLines) {
    const improved = `${prefix}.line${lineNum}improved`;
    if (te(improved)) {
      return improved;
    }
  }
  return `${prefix}.line${lineNum}`;
}

function introFor(item: ToplistingPromotionCardConfig): string {
  if (!props.useImprovedBenefitLines) {
    return '';
  }
  const key = `toplisting.modal.options.${item.i18nKey}.intro`;
  return te(key) ? t(key) : '';
}

function showBenefitLine2FootnoteFor(item: ToplistingPromotionCardConfig): boolean {
  if (!props.useImprovedBenefitLines) {
    return false;
  }
  return item.i18nKey === 'PREMIUM' || item.i18nKey === 'TOP';
}

function benefitLinesFor(item: ToplistingPromotionCardConfig): string[] {
  const prefix = `toplisting.modal.options.${item.i18nKey}`;
  const lines = [t(benefitLineKey(prefix, 1)), t(benefitLineKey(prefix, 2))];
  if (item.displayFullDescription) {
    const line3Key = benefitLineKey(prefix, 3);
    if (te(line3Key)) {
      lines.push(t(line3Key));
    }
  }
  return lines;
}

function onSelect(value: string): void {
  emit('update:modelValue', value);
}

function selectedBackgroundForIndex(index: number): string {
  const palette = TOPLISTING_PROMOTION_CARD_SELECTED_BACKGROUNDS;
  return palette[index % palette.length];
}
</script>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/assets/mixins';

.title {
  margin: 0;
}

.promotionDescription {
  padding-bottom: $space-50;
  margin-top: 0;
  margin-bottom: 1rem;
}

.disclaimer {
  @include body-3($color-grey-90);

  & {
    margin: $space-50 0 $space-150;
  }
}

.promotionRow {
  align-items: stretch;
}

.colCell {
  display: flex;
  flex-direction: column;
}

.cardColumn {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

/* Same top offset in every column so the badge does not shorten the Top card. */
.badgeRail {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 2.25rem;
}

.cardGrow {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}
</style>
